import React, {
  useEffect,
  useState,
  useContext,
  ChangeEvent,
  useLayoutEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import {
  Grid,
  Box,
  Typography,
  Container,
  AppBar,
  Toolbar,
} from '@mui/material';
import dayjs from 'dayjs';
import Sidebar from '../../../components/ConsumerSidebar/ConsumerSidebar';
import LogoutPopup from '../../../components/Inputs/LogoutPopup';
import TemporaryDrawer from '../../../components/TemporaryDrawer/TemporaryDrawer';
import Loader from '../../../components/Loader';
import { getStage } from '../../../services/NetworkServices';
import ApiStatus from '../../../config/apiStatusConstants';
import { storeStageNumber } from '../../../utils/auth-token-utils/cookie';
import { decreaseStage } from '../../../services/NetworkServices';
import {
  changeSubscriptionStatus,
  getSubscriptionStatus,
} from '../../../services/NetworkServices';
import {
  ProfileContext,
  ProfileContextProps,
} from '../../../context/ProfileContext';
import LeftArrow from '../../../assets/Left 1.svg';
import './Subscription.css';

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 45,
  height: 22,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 16,
      padding: 0,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(21px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#FB5012',
        ...theme.applyStyles('dark', {
          backgroundColor: '#177ddc',
        }),
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 18,
    height: 18,
    borderRadius: 9,
    padding: 0,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    opacity: 1,
    backgroundColor: '#7C1717',
    boxSizing: 'border-box',
    ...theme.applyStyles('dark', {
      backgroundColor: 'rgba(255,255,255,.35)',
    }),
  },
}));

const calculateDifference = (expiryDate: string) => {
  const currentDate = dayjs(); // Current date
  // const currentDate = dayjs("2024-12-15"); // Current date
  const expiry = dayjs(expiryDate); // Expiry date from API

  // Calculate the difference in days
  const differenceInDays = expiry.diff(currentDate, 'day');
  return differenceInDays;
};

const Subscription: React.FC = () => {
  const [checked, setChecked] = useState<boolean | null>(null);
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [subscriptionId, setSubscriptionId] = useState<string>('');
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [subscriptionStatusChanged, setSubscriptionStatusChanged] =
    useState<boolean>(false);

  const { showLogoutPopup } = useContext(ProfileContext) as ProfileContextProps;
  const navigate = useNavigate();

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const newChecked = event.target.checked;
    // setApiStatus(ApiStatus.LOADING);
    setChecked(newChecked);

    try {
      const response = await changeSubscriptionStatus({
        cancel_at_period_end: !newChecked,
      });
      const data = await response.data;
      if (data) {
        // setApiStatus(ApiStatus.SUCCESS);
        // setChecked(newChecked);
        setSubscriptionStatusChanged(!subscriptionStatusChanged);

        !expiryDate && fetchSubscriptionStatus();

        console.log('response is', data);
      }
    } catch (error) {
      // setApiStatus(ApiStatus.FAILURE);
      console.error('Error changing subscription status:', error);
      setChecked(!newChecked);
    }
  };

  useEffect(() => {
    const fetchStageDetails = async () => {
      try {
        console.log('inside use effect');
        const response = await getStage();
        const data = await response.data;
        if (data) {
          console.log('stage is', data.data.stage);
          const stage = data.data.stage;
          storeStageNumber(stage);
          if (stage === '1') {
            navigate('/participant/createProfile');
          }
          if (stage === '2') {
            navigate('/participant/inviteBabysitter');
          }
          if (stage === '3') {
            navigate('/participant/selectplan');
          }
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
      }
    };
    fetchStageDetails();
  }, [navigate]);

  const fetchSubscriptionStatus = async () => {
    setApiStatus(ApiStatus.LOADING);
    const response = await getSubscriptionStatus();
    const data = await response.data;
    if (data) {
      setApiStatus(ApiStatus.SUCCESS);
    }
    const subscriptionStatus = data?.data?.subscriptionStatus;
    if (subscriptionStatus !== null) {
      setChecked(!subscriptionStatus.cancel_at_period_end);
      setSubscriptionId(subscriptionStatus.stripeSubscriptionId || '');
      setExpiryDate(subscriptionStatus.cancel_at || '');
    }
  };

  const handleClick = async () => {
    const response = await decreaseStage({ subscriptionId });
    console.log('response is ', response);
    const data = await response.data;
    if (data) {
      navigate('/participant/selectplan');
    }
  };

  const handleBackClick = () => {
    navigate('/participant/dashboard');
  };

  useLayoutEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  return (
    <>
      {showLogoutPopup && <div className="backdrop"> </div>}
      <Box className={`editProfile ${showLogoutPopup ? 'popup-active' : ''}`}>
        <Box className="account-details">
          <Box
            sx={{
              display: { xs: 'block', sm: 'flex' },
              height: { xs: '100vh', sm: '100vh' }, // Ensuring full viewport height
              overflow: { xs: 'auto', sm: 'hidden' }, // Allow scrolling on xs devices if content overflows
            }}
          >
            <AppBar
              position="static"
              sx={{
                display: { xs: 'block', sm: 'none' },
                backgroundColor: 'black',
              }}
            >
              <Toolbar sx={{ background: 'black' }}>
                <TemporaryDrawer />
              </Toolbar>
            </AppBar>

            {/* Sidebar */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                width: { xs: '0%', sm: '35%', md: '28%' },
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  marginLeft: { sm: 2, md: 14 },
                  marginTop: { sm: 2, md: 4 },
                  marginBottom: { sm: 30, md: 4 },
                }}
              >
                <Sidebar />
              </Box>
            </Box>

            {/* Main Content */}
            <Box
              sx={{
                width: { xs: '100%', sm: '65%', md: '72%' },
                flexGrow: 1,
                overflow: 'auto',
                height: '100vh', // Make sure this section takes full height
                paddingY: 4,
              }}
            >
              {apiStatus === ApiStatus.LOADING ? (
                <Loader />
              ) : (
                <Container>
                  <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                    <img
                      src={LeftArrow}
                      onClick={handleBackClick}
                      className="arrow-sm"
                    />
                  </Box>

                  <Box className="edit-profile-pic">
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        paddingTop: '20px',
                        width: { xs: '100%', sm: '100%', md: '80%' },
                        gap: '20px',
                      }}
                    >
                      {/* Card Section */}
                      <Box
                        sx={{
                          borderColor: '#365F91',
                          border: '1.4px solid transparent',
                          background: `
                      linear-gradient(black, black) padding-box,
                      linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%) border-box
                    `,
                          borderImageSlice: 1,
                          borderRadius: '35px',
                          width: '60%',
                          marginTop: { xs: '5vh', md: '10vh' }, // Adjusting for xs devices
                          marginLeft: '20px',
                          padding: '30px',
                        }}
                      >
                        <Typography
                          variant="h3"
                          sx={{
                            color: 'white',
                            fontSize: { xs: '20px', sm: '30px', md: '40px' },
                            fontFamily: 'Opensans',
                            fontWeight: '700',
                            mt: 4,
                            mb: 4,
                          }}
                        >
                          $ 194.78
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'white',
                            fontFamily: 'Opensans',
                            fontWeight: '700',
                            mb: 2,
                          }}
                        >
                          Monthly Plan
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#848484',
                            fontFamily: 'Opensans',
                            fontWeight: '700',
                            marginBottom: '32px',
                          }}
                        >
                          Charges are initiated on the current calendar month
                          and not prorated.
                        </Typography>
                      </Box>

                      {/* Auto Renewal Section */}
                      <Grid item xs={12}>
                        {!(
                          expiryDate && calculateDifference(expiryDate) < 0
                        ) && (
                          <Box
                            sx={{
                              borderColor: '#365F91',
                              border: '1.4px solid transparent',
                              background: `
                          linear-gradient(black, black) padding-box,
                          linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%) border-box
                        `,
                              borderImageSlice: 1,
                              borderRadius: '35px',
                              width: '60%',
                              marginTop: '3vh',
                              marginLeft: '20px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '20px',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'white',
                                fontFamily: 'Opensans',
                                fontWeight: '700',
                              }}
                            >
                              Auto Renewal
                            </Typography>

                            <AntSwitch
                              checked={checked ?? false}
                              onChange={handleChange}
                              inputProps={{ 'aria-label': 'ant design' }}
                            />
                          </Box>
                        )}

                        {expiryDate &&
                          !checked &&
                          calculateDifference(expiryDate) >= 0 && (
                            <Typography
                              sx={{
                                color: 'red',
                                fontFamily: 'Opensans',
                                fontWeight: '700',
                                textAlign: 'left',
                                paddingLeft: '22px',
                                marginTop: '5px',
                              }}
                            >
                              Your Plan will expire on {expiryDate}
                            </Typography>
                          )}

                        {expiryDate &&
                          checked &&
                          calculateDifference(expiryDate) < 0 && (
                            <Box
                              sx={{
                                marginTop: { xs: '4vh', md: '8vh' }, // Adjust for xs devices
                                paddingLeft: '12px',
                                textAlign: 'left',
                              }}
                            >
                              <Typography
                                sx={{
                                  color: 'red',
                                  fontFamily: 'Opensans',
                                  fontWeight: '700',
                                  textAlign: 'left',
                                  paddingLeft: '22px',
                                  marginTop: '5px',
                                }}
                              >
                                Your Plan has already expired on {expiryDate}.
                                Please{' '}
                                <a
                                  onClick={handleClick}
                                  style={{ textDecoration: 'underline' }}
                                >
                                  click here
                                </a>{' '}
                                to renew your plan now.
                              </Typography>
                            </Box>
                          )}
                      </Grid>
                    </Box>
                  </Box>
                </Container>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {showLogoutPopup && <LogoutPopup />}
    </>
  );
};

export default Subscription;
